import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FiArrowLeft, FiArrowRight, FiPause, FiPlay } from "react-icons/fi";
import campaignData from "./campaigns.json";
import campaignCopy from "./campaignCopy";
import styles from "./Styles/DesktopLanding.module.css";

const defaultCampaigns = [...campaignData.docs].sort((a, b) => b.index - a.index);

export default function CampaignCarousel({ campaigns = defaultCampaigns, imageOnly = false, trackId = "campaign-track", labelledBy = "featured-title" }) {
  const track = useRef(null);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(false);

  const advance = useCallback((direction) => {
    const element = track.current;
    if (!element || !element.children.length) return;
    const max = element.scrollWidth - element.clientWidth;
    if (max <= 1) return;
    const position = Math.abs(element.scrollLeft);
    const step = element.children[0].getBoundingClientRect().width + parseFloat(getComputedStyle(element).columnGap);
    const next = direction > 0
      ? (position >= max - 2 ? 0 : Math.min(max, position + step))
      : (position <= 2 ? max : Math.max(0, position - step));
    // RTL scroll origins start at the right edge and move into negative offsets.
    element.scrollTo({ left: -next, behavior: reducedMotion ? "instant" : "smooth" });
  }, [reducedMotion]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(track.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (paused || hovered || focused || reducedMotion || !visible) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) advance(1);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [advance, paused, hovered, focused, reducedMotion, visible]);

  return (
    <div className={styles.campaignCarousel} role="region" aria-roledescription="اسلایدر" aria-labelledby={labelledBy}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <div className={styles.carouselControls}>
        <span>برای دیدن برنامه‌های بیشتر ورق بزنید</span>
        <button type="button" aria-label="برنامه‌های قبلی" aria-controls={trackId} onClick={() => advance(-1)}><FiArrowRight aria-hidden="true" /></button>
        {!reducedMotion && <button type="button" aria-label={paused ? "شروع پخش خودکار" : "توقف پخش خودکار"} aria-pressed={paused} onClick={() => setPaused(!paused)}>{paused ? <FiPlay aria-hidden="true" /> : <FiPause aria-hidden="true" />}</button>}
        <button type="button" aria-label="برنامه‌های بعدی" aria-controls={trackId} onClick={() => advance(1)}><FiArrowLeft aria-hidden="true" /></button>
      </div>
      <div id={trackId} ref={track} className={`${styles.campaignTrack} ${imageOnly ? styles.imageBannerTrack : ""}`} dir="rtl" tabIndex={0} aria-label="برنامه‌های خانواده"
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") { event.preventDefault(); advance(event.key === "ArrowLeft" ? 1 : -1); }
        }}>
        {campaigns.map((campaign) => {
          const copy = (!imageOnly && campaignCopy[campaign._id]) || {
            title: (campaign.title || "").trim(), category: campaign.tags || "برنامه‌های راه بهشت",
            text: campaign.description || "با این برنامه آشنا شوید و جزئیات آن را دنبال کنید.", label: "مشاهده برنامه",
          };
          const external = /^https?:\/\//i.test(campaign.link);
          const Component = campaign.link ? (external ? "a" : Link) : "div";
          const destination = !campaign.link ? {} : external ? { href: campaign.link } : { to: campaign.link };
          return <Component key={campaign._id} {...destination}
            className={imageOnly ? styles.imageBanner : styles.campaignCard}
            target={campaign.linkNewTab ? "_blank" : undefined} rel={campaign.linkNewTab ? "noopener noreferrer" : undefined}>
            <img src={campaign.src} alt={(campaign.title || "").trim()} loading="lazy" />
            {!imageOnly && <div>
              <span className={styles.sectionLabel}>{copy.category}</span>
              <h3>{copy.title}</h3>
              <p>{copy.text}</p>
              <span className={styles.cardLink}>{copy.label}<FiArrowLeft aria-hidden="true" /></span>
            </div>}
          </Component>;
        })}
      </div>
    </div>
  );
}
